import React, { useEffect, useMemo, useState } from "react";
import { api, formatCHF, currentMonthString, formatDatum } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { PageContainer, PageHeader, MonthPicker } from "../components/PageHeader";
import { Pencil } from "lucide-react";

const TYPE_META = {
  verladung_15: { label: "Verladung 15 Min", rate: 6.8, color: "bg-blue-100 text-blue-800" },
  verladung_30: { label: "Verladung 30 Min", rate: 13.5, color: "bg-indigo-100 text-indigo-800" },
  entladung_10: { label: "Entladung 10 Min", rate: 4.6, color: "bg-emerald-100 text-emerald-800" },
};

function expandReports(reports) {
  const out = [];
  for (const r of reports || []) {
    for (const typ of ["verladung_15", "verladung_30", "entladung_10"]) {
      const count = Number(r[typ] || 0);
      if (count > 0) {
        out.push({
          report_id: r.id,
          datum: r.datum,
          fahrer_id: r.fahrer_id,
          fahrer_name: r.fahrer_name,
          fahrzeug_id: r.fahrzeug_id,
          tournummer: r.tournummer || "",
          do_nummer: r.do_nummer || "",
          typ,
          anzahl: count,
          rate: Number(r[`rate_${typ}`] || TYPE_META[typ].rate),
          status: r.status || "offen",
        });
      }
    }
  }
  out.sort((a, b) => (a.datum + a.tournummer).localeCompare(b.datum + b.tournummer));
  return out;
}

export default function VerladungEntladung() {
  const [monat, setMonat] = useState(currentMonthString());
  const [fahrerId, setFahrerId] = useState("_all");
  const [typFilter, setTypFilter] = useState("_all");
  const [employees, setEmployees] = useState([]);
  const [reports, setReports] = useState([]);

  const load = async () => {
    const [e, r] = await Promise.all([
      api.get("/employees"),
      api.get("/daily-reports", { params: { monat } }),
    ]);
    setEmployees(e.data);
    setReports(r.data);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monat]);

  const expanded = useMemo(() => expandReports(reports), [reports]);

  const filtered = expanded.filter(
    (e) => (fahrerId === "_all" || e.fahrer_id === fahrerId) && (typFilter === "_all" || e.typ === typFilter)
  );

  const empName = (id) => employees.find((e) => e.id === id)?.name || "—";

  // Totals per type
  const totals = filtered.reduce(
    (acc, e) => {
      acc[e.typ] = (acc[e.typ] || 0) + e.anzahl;
      acc.umsatz += e.anzahl * e.rate;
      return acc;
    },
    { verladung_15: 0, verladung_30: 0, entladung_10: 0, umsatz: 0 }
  );

  return (
    <PageContainer>
      <PageHeader
        title="Verladungen / Entladungen"
        subtitle="Separate Übersicht pro Datum, Fahrer und Tour — abgeleitet aus den Tagesrapporten"
        testId="ve-page"
      >
        <MonthPicker value={monat} onChange={setMonat} testId="ve-month-picker" />
        <Select value={fahrerId} onValueChange={setFahrerId}>
          <SelectTrigger className="w-44" data-testid="ve-fahrer-filter"><SelectValue placeholder="Fahrer" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Alle Fahrer</SelectItem>
            {employees.filter((e) => e.aktiv).map((e) => (
              <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={typFilter} onValueChange={setTypFilter}>
          <SelectTrigger className="w-44" data-testid="ve-typ-filter"><SelectValue placeholder="Typ" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Alle Typen</SelectItem>
            <SelectItem value="verladung_15">Verladung 15 Min</SelectItem>
            <SelectItem value="verladung_30">Verladung 30 Min</SelectItem>
            <SelectItem value="entladung_10">Entladung 10 Min</SelectItem>
          </SelectContent>
        </Select>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card className="border-neutral-200 bg-white p-3" data-testid="ve-total-v15">
          <div className="text-xs uppercase tracking-wider text-neutral-500">Verladung 15 Min</div>
          <div className="text-2xl font-semibold nivara-num mt-1">{totals.verladung_15}</div>
          <div className="text-xs text-neutral-500 mt-0.5">{formatCHF(totals.verladung_15 * TYPE_META.verladung_15.rate)}</div>
        </Card>
        <Card className="border-neutral-200 bg-white p-3" data-testid="ve-total-v30">
          <div className="text-xs uppercase tracking-wider text-neutral-500">Verladung 30 Min</div>
          <div className="text-2xl font-semibold nivara-num mt-1">{totals.verladung_30}</div>
          <div className="text-xs text-neutral-500 mt-0.5">{formatCHF(totals.verladung_30 * TYPE_META.verladung_30.rate)}</div>
        </Card>
        <Card className="border-neutral-200 bg-white p-3" data-testid="ve-total-e10">
          <div className="text-xs uppercase tracking-wider text-neutral-500">Entladung 10 Min</div>
          <div className="text-2xl font-semibold nivara-num mt-1">{totals.entladung_10}</div>
          <div className="text-xs text-neutral-500 mt-0.5">{formatCHF(totals.entladung_10 * TYPE_META.entladung_10.rate)}</div>
        </Card>
        <Card className="border-neutral-200 bg-white p-3" data-testid="ve-total-umsatz">
          <div className="text-xs uppercase tracking-wider text-neutral-500">Total Umsatz</div>
          <div className="text-2xl font-semibold nivara-num mt-1">{formatCHF(totals.umsatz)}</div>
        </Card>
      </div>

      <Card className="border-neutral-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="ve-table">
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Fahrer</TableHead>
                <TableHead>Tour</TableHead>
                <TableHead>DO</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead className="text-right">Anzahl</TableHead>
                <TableHead className="text-right">Tarif</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-neutral-500 py-8">
                    Keine Verladungen/Entladungen für die aktuelle Auswahl.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((e, i) => (
                <TableRow key={`${e.report_id}-${e.typ}-${i}`}>
                  <TableCell className="font-mono text-xs">{formatDatum(e.datum)}</TableCell>
                  <TableCell>{e.fahrer_id ? empName(e.fahrer_id) : e.fahrer_name || "—"}</TableCell>
                  <TableCell className="font-medium">{e.tournummer || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{e.do_nummer || "—"}</TableCell>
                  <TableCell>
                    <Badge className={TYPE_META[e.typ].color}>{TYPE_META[e.typ].label}</Badge>
                  </TableCell>
                  <TableCell className="text-right nivara-num">{e.anzahl}</TableCell>
                  <TableCell className="text-right nivara-num">{formatCHF(e.rate)}</TableCell>
                  <TableCell className="text-right nivara-num font-semibold">{formatCHF(e.anzahl * e.rate)}</TableCell>
                  <TableCell>
                    <Badge className={e.status === "abgerechnet" ? "bg-[#008A00] text-white" : e.status === "geprüft" ? "bg-[#0055FF] text-white" : "bg-[#FFB800] text-neutral-900"}>{e.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <a href={`/tagestouren?focus=${e.report_id}`} className="text-[#0055FF] hover:underline text-xs inline-flex items-center gap-1" data-testid={`ve-edit-${e.report_id}-${e.typ}`}>
                      <Pencil className="h-3.5 w-3.5" />
                      Tagestour
                    </a>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <div className="text-xs text-neutral-500 mt-3">
        Diese Übersicht wird aus den Tagesrapporten generiert. Änderungen an Verladung/Entladung müssen im jeweiligen Tagesrapport vorgenommen werden.
      </div>
    </PageContainer>
  );
}
